import { db } from "./dbClient";

// ─── Dashboard.jsx ────────────────────────────────────────────────────────────
import { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { useTheme } from "./theme";
import { downloadCSV, printToPDF } from "./exportUtils";
import DateRangeFilter from "./DateRangeFilter";
import QADashboard from "./QADashboard";
import TeamPerformance from "./TeamPerformance";
import BulkCampaignWorkflow from "./BulkCampaignWorkflow";

// ─── Main Dashboard ───────────────────────────────────────────────────────────
export default function Dashboard({ company, currentUser, team, alerts, tmAlerts = [], onNavigate, onOpenCase, customSLAThresholds = {} }) {
  const C = useTheme();
  const { t } = useTranslation();
  const [dateRange,    setDateRange]    = useState({ from: "", to: "" });
  const [assignments,  setAssignments]  = useState([]);
  const [assignLoading, setAssignLoading] = useState(true);
  const [complianceEvents, setComplianceEvents] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");
  const [showBulkCampaign, setShowBulkCampaign] = useState(false);

  // Load MY open assignments — todos, training, cases assigned to me
  useEffect(() => {
    if (!company?.id || !currentUser?.id) { setAssignLoading(false); return; }
    setAssignLoading(true);
    (async () => {
      try {
        // 1. assignments table: training + case + todo entries
        const { data: asgn } = await db.from("assignments")
          .select("*")
          .eq("company_id", company.id)
          .eq("assigned_to", currentUser.id)
          .order("created_at", { ascending: false })
          .limit(20);

        // 2. todos table: personal todos assigned to user
        const { data: todoRows } = await db.from("todos")
          .select("id,title,priority,created_at,user_id")
          .eq("company_id", company.id)
          .eq("user_id", currentUser.id)
          .order("created_at", { ascending: false })
          .limit(20);

        // 3. SARs for company (show all for now - no assigned_to column)
        const { data: sarRows } = await db.from("sar_drafts")
          .select("id,subject_name,created_at")
          .eq("company_id", company.id)
          .order("created_at", { ascending: false })
          .limit(20);

        // 4. CTRs for company (show all for now - no assigned_to column)
        const { data: ctrRows } = await db.from("ctr_filings")
          .select("id,customer_name,created_at")
          .eq("company_id", company.id)
          .order("created_at", { ascending: false })
          .limit(20);

        // Merge: convert todo rows to assignment shape, dedupe by ref_id
        const assignmentIds = new Set((asgn || []).filter(a => a.type === "todo").map(a => a.ref_id));
        const extraTodos = (todoRows || [])
          .filter(t => !assignmentIds.has(t.id))
          .map(t => ({
            id:         `todo-${t.id}`,
            ref_id:     t.id,
            ref_title:  t.title,
            type:       "todo",
            status:     "pending",
            created_at: t.created_at,
            assigned_to: currentUser.id,
          }));

        // Convert SARs
        const sars = (sarRows || []).map(s => ({
          id:         `sar-${s.id}`,
          ref_id:     s.id,
          ref_title:  s.subject_name || "SAR",
          type:       "sar",
          status:     "pending",
          created_at: s.created_at,
          assigned_to: currentUser.id,
        }));

        // Convert CTRs
        const ctrs = (ctrRows || []).map(c => ({
          id:         `ctr-${c.id}`,
          ref_id:     c.id,
          ref_title:  c.customer_name || "CTR",
          type:       "ctr",
          status:     "pending",
          created_at: c.created_at,
          assigned_to: currentUser.id,
        }));

        setAssignments([...(asgn || []), ...extraTodos, ...sars, ...ctrs]
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
          .slice(0, 20)
        );
      } catch (e) {
        console.warn("[dashboard] Failed to load assignments:", e);
      }
      setAssignLoading(false);
    })();
  }, [company?.id, currentUser?.id]);

  // Load upcoming compliance calendar events
  useEffect(() => {
    if (!company?.id) return;
    (async () => {
      try {
        const { data: events } = await db.from("compliance_calendar")
          .select("*")
          .eq("company_id", company.id)
          .order("event_date", { ascending: true })
          .limit(20);

        // Filter to upcoming events (today and future)
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const upcoming = (events || [])
          .filter(e => new Date(e.event_date || e.date) >= today)
          .slice(0, 5);

        setComplianceEvents(upcoming);
      } catch (err) {
        console.warn("[dashboard] Failed to load compliance events:", err);
      }
    })();
  }, [company?.id]);

  // Handle ESC key to close modals
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setShowBulkCampaign(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  async function handleMarkAssignmentDone(a) {
    // Optimistically remove from list
    setAssignments(prev => prev.filter(x => x.id !== a.id));
    try {
      if (a.type === "todo" && a.ref_id) {
        await db.from("todos").update({ done: true }).eq("id", a.ref_id).execute();
      }
      if (!String(a.id).startsWith("todo-")) {
        await db.from("assignments").update({ status: "done" }).eq("id", a.id).execute();
      }
    } catch (e) { console.warn("[markDone]", e); }
  }

  function inRange(row) {
    const d = new Date(row.created_at || row.date || 0);
    if (dateRange.from && d < new Date(dateRange.from)) return false;
    if (dateRange.to   && d > new Date(dateRange.to + "T23:59:59")) return false;
    return true;
  }

  const filteredAlerts   = dateRange.from || dateRange.to ? alerts.filter(inRange)   : alerts;
  const filteredTmAlerts = dateRange.from || dateRange.to ? tmAlerts.filter(inRange) : tmAlerts;

  const cmOpenCount    = filteredAlerts.filter(a => a.status === "open").length;
  const escalatedCount = filteredAlerts.filter(a => a.status === "escalated").length;
  const closedCount    = filteredAlerts.filter(a => a.status === "closed").length;
  const tmOpenCount    = filteredTmAlerts.filter(a => a.status === "open" || a.status === "new" || !a.status).length;
  const tmHighRisk     = filteredTmAlerts.filter(a => a.risk === "HIGH" || a.risk === "Critical" || a.severity === "CRITICAL").length;

  const stats = [
    { label: t('dashboard.stats.caseMgmtAlerts'), sublabel: t('dashboard.stats.addedToCases'),  value: cmOpenCount,             delta: t('dashboard.stats.escalated', { count: escalatedCount }),                    color: C.danger  },
    { label: t('dashboard.stats.tmAlerts'),        sublabel: t('dashboard.stats.fromMonitoring'), value: filteredTmAlerts.length, delta: t('dashboard.stats.openHighRisk', { open: tmOpenCount, highRisk: tmHighRisk }), color: "#F0B429" },
    { label: t('dashboard.stats.closed'),  sublabel: t('dashboard.stats.resolved'),        value: closedCount,             delta: t('dashboard.stats.caseManagement'),                                color: C.success },
    { label: t('dashboard.stats.teamMembers'),     sublabel: t('dashboard.stats.active'),          value: team.length,             delta: t('dashboard.stats.acrossAllRoles'),                               color: C.accent  },
  ];

  function handleExportCSV() {
    downloadCSV(
      filteredAlerts.map(a => ({
        id: a.id, entity: a.entity, type: a.type, amount: a.amount,
        risk: a.risk, status: a.status, country: a.country,
        date: a.date || a.created_at,
      })),
      `crossregguard_dashboard_${Date.now()}.csv`
    );
  }

  function handleExportPDF() {
    const recentRows = filteredAlerts.slice(0, 15).map(a =>
      `<tr><td>${a.entity||"—"}</td><td>${a.type||"—"}</td><td>${a.amount||"—"}</td><td>${a.risk||"—"}</td><td>${a.status||"—"}</td><td>${a.date||a.created_at?.slice(0,10)||"—"}</td></tr>`
    ).join("");
    const teamRows = team.map(m =>
      `<tr><td>${m.firstName||m.first_name||""} ${m.lastName||m.last_name||""}</td><td>${m.role||"—"}</td><td>${m.permission||"analyst"}</td></tr>`
    ).join("");
    const rangeLabel = dateRange.from ? ` (${dateRange.from} → ${dateRange.to || "now"})` : "";
    printToPDF(
      `Dashboard Report${rangeLabel} — ${company?.name || "CrossRegGuard"}`,
      `<div class="stats">
        <div class="stat"><div class="stat-label">CASE MGMT ALERTS</div><div class="stat-value">${cmOpenCount}</div><div class="stat-delta">${escalatedCount} escalated</div></div>
        <div class="stat"><div class="stat-label">TM ALERTS</div><div class="stat-value">${filteredTmAlerts.length}</div><div class="stat-delta">${tmOpenCount} open · ${tmHighRisk} high risk</div></div>
        <div class="stat"><div class="stat-label">CLOSED</div><div class="stat-value">${closedCount}</div><div class="stat-delta">Resolved</div></div>
        <div class="stat"><div class="stat-label">TEAM MEMBERS</div><div class="stat-value">${team.length}</div><div class="stat-delta">Active</div></div>
      </div>
      <div class="section"><div class="section-title">RECENT ALERTS</div>
        <table><thead><tr><th>Entity</th><th>Type</th><th>Amount</th><th>Risk</th><th>Status</th><th>Date</th></tr></thead>
        <tbody>${recentRows}</tbody>
      </table></div>
      <div class="section"><div class="section-title">TEAM</div>
        <table><thead><tr><th>Name</th><th>Role</th><th>Permission</th></tr></thead>
        <tbody>${teamRows}</tbody>
      </table></div>`,
      company?.name
    );
  }

  return (
    <div>

      {/* Header */}
      <div style={{ marginBottom: 24, display: "flex", justifyContent: "space-between",
        alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ color: C.text, fontFamily: "'Syne',sans-serif", fontSize: 26,
            fontWeight: 700, margin: 0 }}>
            {t('dashboard.welcomeBack', { name: currentUser?.firstName || 'User' })}
          </h1>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 5 }}>
            <p style={{ color: C.textMid, margin: 0, fontSize: 14 }}>
              {company?.name} · {company?.jurisdiction}
            </p>
            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <div style={{ width: 6, height: 6, borderRadius: "50%",
                background: C.success, animation: "crg_pulse 2s ease-in-out infinite" }} />
              <span style={{ color: C.textDim, fontSize: 10 }}>{t('common.live')}</span>
              <style>{`@keyframes crg_pulse{0%,100%{opacity:1}50%{opacity:0.35}}`}</style>
            </div>
          </div>
          <div style={{ color: C.textDim, fontSize: 12, marginTop: 8 }}>
            Today is {new Date().toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          {activeTab === "overview" && (
            <>
              <button onClick={() => setShowBulkCampaign(true)}
                style={{ background: C.accent, border: "none",
                  borderRadius: 7, color: C.bg, padding: "7px 14px",
                  cursor: "pointer", fontSize: 12, fontFamily: "'DM Mono',monospace", fontWeight: 600 }}>
                + Bulk Campaign
              </button>
              <button onClick={handleExportCSV}
                style={{ background: "transparent", border: `1px solid ${C.border}`,
                  borderRadius: 7, color: C.textMid, padding: "7px 14px",
                  cursor: "pointer", fontSize: 12, fontFamily: "'DM Mono',monospace" }}>
                {t('common.exportCSV')}
              </button>
              <button onClick={handleExportPDF}
                style={{ background: "transparent", border: `1px solid ${C.accent}`,
                  borderRadius: 7, color: C.accent, padding: "7px 14px",
                  cursor: "pointer", fontSize: 12, fontFamily: "'DM Mono',monospace" }}>
                {t('common.exportPDF')}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <div style={{ display: "flex", gap: 16, borderBottom: `1px solid ${C.border}`, marginBottom: 24, paddingBottom: 0 }}>
        {[
          { id: "overview", label: "Overview", icon: "⬡" },
          { id: "qa", label: "QA Dashboard", icon: "🔍" },
          { id: "team_performance", label: "Team Performance", icon: "👥" }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "12px 0",
              borderBottom: activeTab === tab.id ? `2px solid ${C.accent}` : "2px solid transparent",
              background: "transparent",
              border: "none",
              color: activeTab === tab.id ? C.accent : C.textMid,
              cursor: "pointer",
              fontSize: 13,
              fontWeight: activeTab === tab.id ? 600 : 500,
              fontFamily: "'DM Mono',monospace",
              transition: "all 0.2s"
            }}
            onMouseEnter={e => {
              if (activeTab !== tab.id) {
                e.currentTarget.style.color = C.text;
              }
            }}
            onMouseLeave={e => {
              if (activeTab !== tab.id) {
                e.currentTarget.style.color = C.textMid;
              }
            }}
          >
            <span>{tab.icon}</span>
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Conditional Content */}
      {activeTab === "qa" ? (
        <QADashboard company={company} currentUser={currentUser} team={team} />
      ) : activeTab === "team_performance" ? (
        <TeamPerformance company={company} team={team} />
      ) : (
        <>
          {/* Date filter */}
          <div style={{ marginBottom: 20 }}>
            <DateRangeFilter from={dateRange.from} to={dateRange.to} onChange={setDateRange} />
          </div>

          {/* Stat cards */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14, marginBottom: 24 }}>
        {stats.map((s, idx) => (
          <div key={s.label}
            onClick={() => {
              if (idx === 0) onNavigate("cases");
              else if (idx === 1) onNavigate("tm");
              else if (idx === 2) onNavigate("cases");
              else if (idx === 3) {
                setTimeout(() => {
                  const elem = document.getElementById("dashboard-team-section");
                  if (elem) elem.scrollIntoView({ behavior: "smooth", block: "start" });
                }, 100);
              }
            }}
            style={{ background: C.surface, border: `1px solid ${C.border}`,
              borderRadius: 10, padding: "18px 20px", borderTop: `2px solid ${s.color}`,
              cursor: "pointer", transition: "all 0.2s",
              transform: "translateY(0)" }}
            onMouseEnter={e => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = `0 4px 12px ${C.border}`;
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "none";
            }}>
            <div style={{ color: C.textMid, fontSize: 11, marginBottom: 2, letterSpacing: "0.08em" }}>
              {s.label.toUpperCase()}
            </div>
            <div style={{ color: C.textDim, fontSize: 10, marginBottom: 8, letterSpacing: "0.06em" }}>
              {s.sublabel.toUpperCase()}
            </div>
            <div style={{ color: s.color, fontFamily: "'Syne',sans-serif",
              fontSize: 30, fontWeight: 700, lineHeight: 1 }}>
              {s.value}
            </div>
            <div style={{ color: C.textDim, fontSize: 11, marginTop: 6 }}>{s.delta}</div>
          </div>
          ))}
          </div>

          {/* Alert source bar */}
          {(filteredAlerts.length > 0 || filteredTmAlerts.length > 0) && (() => {
            const total = filteredAlerts.length + filteredTmAlerts.length;
            if (total === 0) return null;
            const cmPct = Math.round((filteredAlerts.length / total) * 100);
            const tmPct = 100 - cmPct;
            return (
              <div style={{ background: C.surface, border: `1px solid ${C.border}`,
                borderRadius: 10, padding: "14px 20px", marginBottom: 24 }}>
            <div style={{ display: "flex", justifyContent: "space-between",
              alignItems: "center", marginBottom: 10 }}>
              <div style={{ color: C.text, fontSize: 13, fontWeight: 600 }}>{t('dashboard.alertSources')}</div>
              <div style={{ color: C.textDim, fontSize: 11 }}>{t('dashboard.total', { count: total })}</div>
            </div>
            <div style={{ display: "flex", height: 6, borderRadius: 4,
              overflow: "hidden", marginBottom: 10 }}>
              <div style={{ width: `${cmPct}%`, background: C.danger, transition: "width 0.4s" }} />
              <div style={{ width: `${tmPct}%`, background: "#F0B429", transition: "width 0.4s" }} />
            </div>
            <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
              {[
                { label: t('sidebar.nav.cases'),        count: filteredAlerts.length,   pct: cmPct, color: C.danger  },
                { label: t('sidebar.nav.tm'), count: filteredTmAlerts.length, pct: tmPct, color: "#F0B429" },
              ].map(({ label, count, pct, color }) => (
                <div key={label} style={{ display: "flex", alignItems: "center", gap: 7 }}>
                  <div style={{ width: 8, height: 8, borderRadius: 2, background: color, flexShrink: 0 }} />
                  <span style={{ color: C.textMid, fontSize: 12 }}>{label}</span>
                  <span style={{ color: C.text, fontSize: 12,
                    fontFamily: "'DM Mono',monospace", fontWeight: 600 }}>
                    {count} ({pct}%)
                  </span>
                </div>
              ))}
              </div>
            </div>
          );
          })()}

          {/* Case Status Summary Widget */}
          {(() => {
            const openCases = filteredAlerts.filter(a => a.status === "open" || a.status === "new" || a.status === "in_review" || a.status === "pending_review").length;
            const escalatedCases = filteredAlerts.filter(a => a.status === "escalated").length;
            const closedCases = filteredAlerts.filter(a => a.status === "closed" || a.status === "merged").length;
            const underReviewCases = filteredAlerts.filter(a => a.status === "under_review").length;

            return (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 24 }}>
                <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "16px 20px", textAlign: "center" }}>
                  <div style={{ color: C.text, fontWeight: 600, fontSize: 14, marginBottom: 8 }}>Open Cases</div>
                  <div style={{ color: C.accent, fontFamily: "'Syne',sans-serif", fontSize: 28, fontWeight: 700 }}>
                    {openCases}
                  </div>
                  <div style={{ color: C.textDim, fontSize: 10, marginTop: 4 }}>Awaiting review</div>
                </div>

                <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "16px 20px", textAlign: "center" }}>
                  <div style={{ color: C.text, fontWeight: 600, fontSize: 14, marginBottom: 8 }}>Under Review</div>
                  <div style={{ color: "#FFA500", fontFamily: "'Syne',sans-serif", fontSize: 28, fontWeight: 700 }}>
                    {underReviewCases}
                  </div>
                  <div style={{ color: C.textDim, fontSize: 10, marginTop: 4 }}>In progress</div>
                </div>

                <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "16px 20px", textAlign: "center" }}>
                  <div style={{ color: C.text, fontWeight: 600, fontSize: 14, marginBottom: 8 }}>Escalated</div>
                  <div style={{ color: "#FF6B6B", fontFamily: "'Syne',sans-serif", fontSize: 28, fontWeight: 700 }}>
                    {escalatedCases}
                  </div>
                  <div style={{ color: C.textDim, fontSize: 10, marginTop: 4 }}>Requires action</div>
                </div>

                <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "16px 20px", textAlign: "center" }}>
                  <div style={{ color: C.text, fontWeight: 600, fontSize: 14, marginBottom: 8 }}>Closed</div>
                  <div style={{ color: C.success, fontFamily: "'Syne',sans-serif", fontSize: 28, fontWeight: 700 }}>
                    {closedCases}
                  </div>
                  <div style={{ color: C.textDim, fontSize: 10, marginTop: 4 }}>Resolved & merged</div>
                </div>
              </div>
            );
          })()}

          {/* Case Aging Widget */}
          {(() => {
            // Use custom SLA thresholds or defaults
            const SLA_DAYS = {
              CRITICAL: customSLAThresholds?.CRITICAL ?? 3,
              HIGH: customSLAThresholds?.HIGH ?? 7,
              MEDIUM: customSLAThresholds?.MEDIUM ?? 14,
              LOW: customSLAThresholds?.LOW ?? 21,
            };
            const now = new Date();

            // Function to calculate SLA deadline days remaining
            function getSLADaysRemaining(caseRow) {
              const slaLimit = SLA_DAYS[(caseRow.risk || "").toUpperCase()];
              if (!slaLimit) return null;
              const alertDate = new Date(caseRow.alert_generated_at || caseRow.created_at || 0);
              const daysSince = Math.floor((now - alertDate) / (1000 * 60 * 60 * 24));
              return slaLimit - daysSince;
            }

            // Filter for open cases only
            const openCases = filteredAlerts.filter(a => a.status === "open");

            // Calculate days overdue for each case
            const overdueBuckets = [
              {
                label: "On Track",
                color: C.success,
                cases: openCases.filter(a => {
                  const remaining = getSLADaysRemaining(a);
                  return remaining !== null && remaining >= 0;
                })
              },
              {
                label: "1–7 days overdue",
                color: "#FFA500",
                cases: openCases.filter(a => {
                  const remaining = getSLADaysRemaining(a);
                  return remaining !== null && remaining < 0 && remaining >= -7;
                })
              },
              {
                label: "7–14 days overdue",
                color: "#FF8C00",
                cases: openCases.filter(a => {
                  const remaining = getSLADaysRemaining(a);
                  return remaining !== null && remaining < -7 && remaining >= -14;
                })
              },
              {
                label: "14–21 days overdue",
                color: "#FF6B6B",
                cases: openCases.filter(a => {
                  const remaining = getSLADaysRemaining(a);
                  return remaining !== null && remaining < -14 && remaining >= -21;
                })
              },
              {
                label: "+21 days overdue",
                color: C.danger,
                cases: openCases.filter(a => {
                  const remaining = getSLADaysRemaining(a);
                  return remaining !== null && remaining < -21;
                })
              }
            ];

            const overdueCount = overdueBuckets.slice(1).reduce((sum, b) => sum + b.cases.length, 0);
            const closedCases = filteredAlerts.filter(a => a.status === "closed" || a.status === "merged").length;
            const openWithoutRisk = openCases.filter(a => !getSLADaysRemaining(a) && !SLA_DAYS[(a.risk || "").toUpperCase()]).length;
            const casesEvaluated = overdueBuckets.reduce((sum, b) => sum + b.cases.length, 0);

            return (
              <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "16px 20px", marginBottom: 24 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                  <div style={{ color: C.text, fontWeight: 600, fontSize: 14 }}>Case Overdue Status</div>
                  {overdueCount > 0 && (
                    <span style={{ background: `${C.danger}22`, color: C.danger, fontSize: 10, padding: "2px 8px", borderRadius: 10, fontWeight: 700 }}>
                      ⚠ {overdueCount} case{overdueCount > 1 ? "s" : ""} overdue
                    </span>
                  )}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 10 }}>
                  {overdueBuckets.map(b => (
                    <div key={b.label}
                      onClick={() => onNavigate("cases", { filter: b.label })}
                      style={{
                        background: `${b.color}12`,
                        border: `1px solid ${b.color}44`,
                        borderRadius: 8,
                        padding: "12px 14px",
                        textAlign: "center",
                        cursor: "pointer",
                        transition: "all 0.2s"
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.background = `${b.color}20`;
                        e.currentTarget.style.borderColor = `${b.color}66`;
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.background = `${b.color}12`;
                        e.currentTarget.style.borderColor = `${b.color}44`;
                      }}>
                      <div style={{ color: b.color, fontFamily: "'Syne',sans-serif", fontSize: 28, fontWeight: 700, lineHeight: 1 }}>
                        {b.cases.length}
                      </div>
                      <div style={{ color: C.textDim, fontSize: 10, marginTop: 4 }}>{b.label}</div>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${C.border}`, color: C.textDim, fontSize: 11, lineHeight: 1.6 }}>
                  <div style={{ marginBottom: 6 }}>
                    <strong style={{ color: C.textMid }}>Cases evaluated:</strong> {casesEvaluated} open with valid risk level
                  </div>
                  <div style={{ fontSize: 10, color: C.textDim, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    <div>Closed: {closedCases}</div>
                    <div>No risk level: {openWithoutRisk}</div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Compliance Calendar Widget */}
          <div style={{ background: C.surface, border: `1px solid ${C.border}`,
            borderRadius: 10, padding: 20, marginBottom: 24 }}>
            <div style={{ display: "flex", justifyContent: "space-between",
              alignItems: "center", marginBottom: 14 }}>
              <div style={{ color: C.text, fontWeight: 600, fontSize: 14 }}>
                📅 {t('dashboard.upcomingCompliance', { defaultValue: 'Upcoming Compliance Events' })}
          </div>
          <button onClick={() => onNavigate("calendar")}
            style={{ background: "transparent", border: "none", color: C.accent,
              fontSize: 11, cursor: "pointer", fontFamily: "'DM Mono',monospace" }}>
            {t('common.viewAll')}
          </button>
        </div>
        {complianceEvents.length === 0 ? (
          <div style={{ color: C.textDim, fontSize: 12, padding: "20px 0", textAlign: "center" }}>
            No upcoming compliance events.{' '}
            <button onClick={() => onNavigate("calendar")}
              style={{ background: "transparent", border: "none", color: C.accent,
                cursor: "pointer", textDecoration: "underline", fontSize: 12,
                fontFamily: "inherit", padding: 0 }}>
              Add events to calendar
            </button>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {complianceEvents.map((event, idx) => (
              <div key={event.id || idx}
                onClick={() => onNavigate("calendar")}
                style={{ display: "flex", alignItems: "center", gap: 12,
                  padding: "10px 12px", borderRadius: 8, background: C.surfaceAlt,
                  border: `1px solid ${C.border}`, cursor: "pointer",
                  transition: "all 0.2s" }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = C.bg;
                  e.currentTarget.style.borderColor = C.accent;
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = C.surfaceAlt;
                  e.currentTarget.style.borderColor = C.border;
                }}>
                <div style={{ flex: 1 }}>
                  <div style={{ color: C.text, fontSize: 12, fontWeight: 500 }}>
                    {event.event_title || event.title || "Compliance Event"}
                  </div>
                  <div style={{ color: C.textDim, fontSize: 10, marginTop: 2 }}>
                    {new Date(event.event_date || event.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                    {event.event_type && ` · ${event.event_type}`}
                  </div>
                </div>
                {event.priority && (
                  <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4,
                    fontFamily: "'DM Mono',monospace",
                    background: event.priority === 'high' ? `${C.danger}20` : event.priority === 'medium' ? `${C.warn}20` : `${C.success}20`,
                    color: event.priority === 'high' ? C.danger : event.priority === 'medium' ? C.warn : C.success }}>
                    {event.priority.toUpperCase()}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bottom grid: Recent Assignments + Team */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>

        {/* My Open Assignments */}
        <div style={{ background: C.surface, border: `1px solid ${C.border}`,
          borderRadius: 10, padding: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between",
            alignItems: "center", marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ color: C.text, fontWeight: 600, fontSize: 14 }}>
                {t('dashboard.myOpenAssignments')}
              </div>
              {!assignLoading && assignments.length > 0 && (
                <span style={{ background: C.accentGlow, color: C.accent,
                  fontSize: 10, padding: "1px 7px", borderRadius: 10,
                  fontFamily: "'DM Mono',monospace", fontWeight: 600 }}>
                  {assignments.length}
                </span>
              )}
            </div>
            <button onClick={() => onNavigate("todo")}
              style={{ background: "transparent", border: "none", color: C.accent,
                fontSize: 11, cursor: "pointer", fontFamily: "'DM Mono',monospace" }}>
              {t('common.viewAll')}
            </button>
          </div>
          {assignLoading ? (
            <div style={{ color: C.textDim, fontSize: 12, padding: "16px 0", textAlign: "center" }}>
              {t('common.loading')}
            </div>
          ) : assignments.length === 0 ? (
            <div style={{ color: C.textDim, fontSize: 12, padding: "16px 0", textAlign: "center" }}>
              {t('dashboard.noOpenAssignments')}
            </div>
          ) : assignments.map(a => {
            const typeColor = a.type === "case" ? C.danger : a.type === "training" ? C.accent : a.type === "edd" ? C.purple : a.type === "sar" ? C.warn : a.type === "ctr" ? C.success : C.textDim;
            const typeLabel = a.type === "case" ? t('dashboard.assignmentTypes.case') : a.type === "training" ? t('dashboard.assignmentTypes.training') : a.type === "edd" ? t('dashboard.assignmentTypes.edd') : a.type === "sar" ? "SAR" : a.type === "ctr" ? "CTR" : t('dashboard.assignmentTypes.task');
            const navTarget = a.type === "case" ? "cases" : a.type === "training" ? "training" : a.type === "edd" ? "edd" : a.type === "sar" ? "sars" : a.type === "ctr" ? "ctrs" : "todo";
            return (
              <div key={a.id}
                onClick={async () => {
                  if (a.type === "case") {
                    const caseObj = alerts.find(c => String(c.id) === String(a.ref_id));
                    if (caseObj && onOpenCase) { onOpenCase(caseObj); return; }
                    // Fallback: fetch from DB in case it's not in local alerts
                    if (onOpenCase && a.ref_id) {
                      const { data } = await db.from("cases").select("*").eq("id", a.ref_id).single();
                      if (data) { onOpenCase(data); return; }
                    }
                    onNavigate("cases");
                    return;
                  }
                  onNavigate(navTarget);
                }}
                style={{ display: "flex", alignItems: "center", gap: 10,
                  padding: "9px 0", borderBottom: `1px solid ${C.border}`,
                  cursor: "pointer" }}>
                <div style={{ width: 26, height: 26, borderRadius: 6, flexShrink: 0,
                  background: `${typeColor}18`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 13 }}>
                  {a.type === "case" ? "⚠" : a.type === "training" ? "📋" : a.type === "edd" ? "◈" : a.type === "sar" ? "📝" : a.type === "ctr" ? "💵" : "✓"}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: C.text, fontSize: 12,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {a.ref_title || a.ref_id}
                  </div>
                  <div style={{ color: C.textDim, fontSize: 10, marginTop: 1 }}>
                    {new Date(a.created_at).toLocaleDateString()}
                  </div>
                </div>
                <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 4,
                  fontFamily: "'DM Mono',monospace", flexShrink: 0,
                  background: `${typeColor}18`, color: typeColor }}>
                  {typeLabel}
                </span>
                <button
                  onClick={e => { e.stopPropagation(); handleMarkAssignmentDone(a); }}
                  title="Mark done"
                  style={{ flexShrink: 0, width: 24, height: 24, borderRadius: 5, border: `1px solid ${C.border}`, background: "transparent", color: C.success, cursor: "pointer", fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}>
                  ✓
                </button>
              </div>
            );
          })}
        </div>

        {/* Team */}
        <div id="dashboard-team-section" style={{ background: C.surface, border: `1px solid ${C.border}`,
          borderRadius: 10, padding: 20 }}>
          <div style={{ color: C.text, fontWeight: 600, marginBottom: 14, fontSize: 14 }}>
            {t('dashboard.team')}
          </div>
          {team.length === 0 ? (
            <div style={{ color: C.textDim, fontSize: 12, padding: "16px 0", textAlign: "center" }}>
              {t('dashboard.noTeamMembers')}
            </div>
          ) : team.map(m => (
            <div key={m.id} onClick={() => onNavigate('chat', { dmPerson: m })}
              style={{ display: "flex", alignItems: "center", gap: 10,
              padding: "8px 0", borderBottom: `1px solid ${C.border}`,
              cursor: "pointer", transition: "all 0.2s",
              _hover: { background: C.surfaceAlt, borderRadius: 6, paddingLeft: 8, paddingRight: 8 }
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = C.surfaceAlt; e.currentTarget.style.borderRadius = "6px"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>
              <div style={{ width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
                background: `linear-gradient(135deg,${C.borderLight},${C.border})`,
                display: "flex", alignItems: "center", justifyContent: "center",
                color: C.textMid, fontSize: 10, fontWeight: 700 }}>
                {m.firstName?.[0]}{m.lastName?.[0]}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ color: C.text, fontSize: 13 }}>{m.firstName} {m.lastName}</div>
                <div style={{ color: C.textDim, fontSize: 11 }}>{m.role}</div>
              </div>
              <span style={{ color: C.accent, fontSize: 10, background: C.accentGlow,
                padding: "2px 7px", borderRadius: 4 }}>
                {m.permission ? t(`common.permissions.${m.permission}`, { defaultValue: m.permission }) : ""}
              </span>
            </div>
          ))}
        </div>

          </div>
        </>
      )}

      {/* Bulk Campaign Workflow Modal */}
      {showBulkCampaign && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.5)',
          zIndex: 300,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          overflowY: 'auto'
        }} onClick={() => setShowBulkCampaign(false)}>
          <div onClick={e => e.stopPropagation()} style={{
            width: '100%',
            maxWidth: '1200px',
            maxHeight: '90vh',
            overflowY: 'auto',
            background: C.bg,
            border: `1px solid ${C.border}`,
            borderRadius: 12,
            position: 'relative'
          }}>
            <button
              onClick={() => setShowBulkCampaign(false)}
              style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                background: 'transparent',
                border: 'none',
                fontSize: '1.5rem',
                cursor: 'pointer',
                color: C.text,
                zIndex: 301
              }}
              title="Close (Esc)"
            >
              ✕
            </button>
            <BulkCampaignWorkflow company={company} currentUser={currentUser} onClose={() => setShowBulkCampaign(false)} />
          </div>
        </div>
      )}
    </div>
  );
}